import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup,
    Spinner
} from "react-bootstrap";

const BASE =
    process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";

const API_URL = `${BASE}/api/news`;
const IMAGE_URL = `${BASE}/uploads/`;

const Sport = () => {
    const [sportsNews, setSportsNews] = useState([]);
    const [show, setShow] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSportsNews();
    }, []);

    // ================= IMAGE =================
    const getImage = (item) => {
        if (item?.imagePath?.startsWith("http")) return item.imagePath;
        if (item?.image) return `${IMAGE_URL}${item.image}`;
        return "https://via.placeholder.com/400x250?text=No+Image";
    };

    // ================= VIEWS =================
    const getViews = (item) => {
        const v =
            item?.views ??
            item?.viewCount ??
            item?.totalViews ??
            item?.impressions ??
            0;

        return Number(v) || 0;
    };

    const formatViews = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n;
    };

    // ================= FETCH =================
    const fetchSportsNews = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const data = result?.data || [];

            const sports = data.filter(
                (item) => item?.category?.toLowerCase() === "sports"
            );

            setSportsNews(sports);
        } catch (err) {
            console.error("Sports Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // ================= VIEW =================
    const handleShow = (item) => {
        const id = item?._id || item?.id;
        const nextViews = getViews(item) + 1;

        if (id) {
            setSportsNews((prev) =>
                prev.map((n) =>
                    (n._id === id || n.id === id)
                        ? { ...n, views: getViews(n) + 1 }
                        : n
                )
            );
        }

        setSelectedNews({ ...item, views: nextViews });
        setShow(true);

        fetch(`${API_URL}/${id}/view`, { method: "PUT" }).catch(() => { });
    };

    const handleClose = () => setShow(false);

    // ================= SHARE =================
    const getShareUrl = () => window.location.href;

    const openNewTab = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link copied!");
        } catch {
            window.prompt("Copy this link:", text);
        }
    };

    const shareTo = (platform, post) => {
        const url = getShareUrl();
        const title = post?.title || "News";

        switch (platform) {
            case "whatsapp":
                openNewTab(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`);
                break;
            case "facebook":
                openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
                break;
            case "x":
                openNewTab(`https://twitter.com/intent/tweet?text=${title}&url=${url}`);
                break;
            case "telegram":
                openNewTab(`https://t.me/share/url?url=${url}&text=${title}`);
                break;
            case "linkedin":
                openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
                break;
            case "copy":
                copyToClipboard(url);
                break;
            default:
                break;
        }
    };

    const ShareMenu = ({ post }) => (
        <Dropdown as={ButtonGroup}>
            <Button size="sm" variant="danger">
                <i className="bi bi-share-fill me-1"></i> Share
            </Button>

            <Dropdown.Toggle split size="sm" variant="danger" />

            <Dropdown.Menu>
                <Dropdown.Item onClick={() => shareTo("whatsapp", post)}>
                    WhatsApp
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("facebook", post)}>
                    Facebook
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("x", post)}>
                    X
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("telegram", post)}>
                    Telegram
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("linkedin", post)}>
                    LinkedIn
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => shareTo("copy", post)}>
                    Copy Link
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    return (
        <>
            {/* HERO */}
            <div className="sport-hero text-white text-center py-5">
                <Container>
                    <h1 className="fw-bold display-4">Sports News</h1>
                    <p className="lead">
                        Latest match results, tournaments & player updates
                    </p>
                </Container>
            </div>

            {/* NEWS */}
            <Container className="py-5">
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                ) : sportsNews.length === 0 ? (
                    <h5 className="text-center text-muted">
                        No Sports News Available
                    </h5>
                ) : (
                    <Row className="g-4">
                        {sportsNews.map((news, index) => (
                            <Col md={4} key={index}>
                                <Card className="sport-card h-100">

                                    <Card.Img
                                        src={getImage(news)}
                                        className="sport-img"
                                    />

                                    <Card.Body>
                                        <div className="d-flex justify-content-between mb-2">
                                            <Badge bg="danger">
                                                {news.category}
                                            </Badge>

                                            <small>
                                                <i className="bi bi-eye-fill me-1"></i>
                                                {formatViews(getViews(news))}
                                            </small>
                                        </div>

                                        <Card.Title>{news.title}</Card.Title>

                                        <Card.Text className="text-muted">
                                            {news.shortDesc}
                                        </Card.Text>

                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleShow(news)}
                                            >
                                                Read More
                                            </Button>

                                            <ShareMenu post={news} />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            {/* MODAL */}
            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            <img
                                src={getImage(selectedNews)}
                                className="w-100 rounded mb-3"
                                alt=""
                            />

                            <p>{selectedNews.content}</p>

                            <div className="d-flex justify-content-between">
                                <span>
                                    <i className="bi bi-eye-fill me-1"></i>
                                    {formatViews(getViews(selectedNews))}
                                </span>

                                <ShareMenu post={selectedNews} />
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* STYLE */}
            <style>{`
                .sport-hero {
                    background: linear-gradient(135deg, #dc3545, #6c0d14);
                }

                .sport-card {
                    border-radius: 12px;
                    transition: 0.3s;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.08);
                }

                .sport-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 18px 40px rgba(0,0,0,0.15);
                }

                .sport-img {
                    height: 220px;
                    object-fit: cover;
                }
            `}</style>
        </>
    );
};

export default Sport;